import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, CreditCard, Plus, PencilSimple, Trash, CheckCircle,
  XCircle, FloppyDisk, X, Lightbulb,
} from "@phosphor-icons/react";
import AdminSection from "../../components/admin/AdminSection";
import apiClient from "../../lib/api";
import { toast } from "sonner";

const METHOD_TYPES = [
  { value: "zelle",      label: "Zelle",           color: "text-purple-400",  bg: "bg-purple-500/10",  border: "border-purple-500/20" },
  { value: "pago_movil", label: "Pago Móvil",       color: "text-blue-400",    bg: "bg-blue-500/10",    border: "border-blue-500/20" },
  { value: "binance",    label: "Binance (USDT)",   color: "text-yellow-400",  bg: "bg-yellow-500/10",  border: "border-yellow-500/20" },
];

const getTypeMeta = (type) => METHOD_TYPES.find(m => m.value === type) || METHOD_TYPES[0];

const EMPTY_FORM = { type: "zelle", name: "", account_info: "", account_name: "", instructions: "", is_active: true };

function MethodModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.account_info.trim()) {
      toast.error("Nombre y datos de cuenta son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="surface rounded-2xl border border-white/[0.1] w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-lg">{initial ? "Editar método" : "Nuevo método de pago"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!initial && (
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-2">Tipo</label>
            <div className="flex gap-2 flex-wrap">
              {METHOD_TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => set("type", t.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                    form.type === t.value
                      ? `${t.bg} ${t.color} ${t.border}`
                      : "border-white/[0.08] text-zinc-500 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {[
          { key: "name",         label: "Nombre visible al cliente", placeholder: "ej: Zelle Personal" },
          { key: "account_info", label: "Datos de la cuenta",        placeholder: "ej: pagos@email.com / 0414-1234567 / ID Binance" },
          { key: "account_name", label: "Nombre del titular",        placeholder: "ej: Ronald García" },
          { key: "instructions", label: "Instrucciones para el cliente", placeholder: "ej: Envía el pago y sube la captura de pantalla", multiline: true },
        ].map(field => (
          <div key={field.key}>
            <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">{field.label}</label>
            {field.multiline ? (
              <textarea
                className="input-field w-full resize-none h-20 text-sm"
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={e => set(field.key, e.target.value)}
              />
            ) : (
              <input
                className="input-field w-full text-sm"
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={e => set(field.key, e.target.value)}
              />
            )}
          </div>
        ))}

        <div className="flex items-center gap-3">
          <button
            onClick={() => set("is_active", !form.is_active)}
            data-testid="pm-toggle-active"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
              form.is_active
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                : "border-white/[0.08] text-zinc-500"
            }`}
          >
            {form.is_active ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {form.is_active ? "Activo" : "Inactivo"}
          </button>
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1 text-sm">Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="pm-save-btn"
            className="btn-primary flex-1 text-sm disabled:opacity-50"
          >
            <FloppyDisk className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPaymentMethodsPage() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode: 'create'} | {mode: 'edit', method}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/payment-methods");
      setMethods(res.data.methods || []);
    } catch {
      toast.error("Error al cargar métodos de pago");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal?.mode === "edit") {
      await apiClient.put(`/admin/payment-methods/${modal.method.id}`, form);
      toast.success("Método actualizado");
    } else {
      await apiClient.post("/admin/payment-methods", form);
      toast.success("Método creado");
    }
    await load();
  };

  const toggleActive = async (m) => {
    try {
      await apiClient.put(`/admin/payment-methods/${m.id}`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Método desactivado" : "Método activado");
      await load();
    } catch {
      toast.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`¿Eliminar "${m.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await apiClient.delete(`/admin/payment-methods/${m.id}`);
      toast.success("Método eliminado");
      await load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {modal && (
        <MethodModal
          initial={modal.mode === "edit" ? modal.method : null}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {/* Header */}
      <div className="border-b border-white/[0.06] bg-surface/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-500 hover:text-foreground transition-colors">
              <CaretLeft weight="bold" className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <CreditCard weight="duotone" className="w-5 h-5 text-yellow-400" />
              <span className="font-bold text-lg">Métodos de Pago</span>
            </div>
          </div>
          <button
            onClick={() => setModal({ mode: "create" })}
            data-testid="pm-create-btn"
            className="btn-primary text-sm"
          >
            <Plus weight="bold" className="w-4 h-4" /> Nuevo método
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8 space-y-4">
        <p className="text-sm text-zinc-500">
          Configura los datos de tus cuentas para recibir pagos manuales. Los clientes verán estos datos al seleccionar un método de pago.
        </p>

        <AdminSection
          icon={CreditCard}
          title="Tus métodos de pago"
          subtitle="Cuentas donde recibes los pagos de tus clientes"
          color="amber"
        >
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="rounded-xl h-24 animate-pulse bg-white/[0.04]" />)}
            </div>
          ) : methods.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
              <p className="text-zinc-500 mb-4">No hay métodos de pago configurados</p>
              <button onClick={() => setModal({ mode: "create" })} className="btn-primary text-sm">
                <Plus weight="bold" className="w-4 h-4" /> Agregar primer método
              </button>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-white/[0.06]">
              <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {["Método", "Datos de cuenta", "Titular", "Estado", "Acciones"].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left text-[11px] text-zinc-500 uppercase tracking-wider font-bold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {methods.map((m, i) => {
                  const meta = getTypeMeta(m.type);
                  return (
                    <tr key={m.id} data-testid={`pm-row-${i}`} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-4">
                        <span className={`chip text-[11px] border font-bold ${meta.bg} ${meta.color} ${meta.border}`}>
                          {m.name}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs text-zinc-300">{m.account_info || "—"}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-zinc-300">{m.account_name || "—"}</span>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => toggleActive(m)}
                          data-testid={`pm-toggle-${i}`}
                          className={`chip text-[11px] border cursor-pointer transition-all hover:opacity-80 ${
                            m.is_active
                              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                              : "bg-red-500/10 text-red-400 border-red-500/20"
                          }`}
                        >
                          {m.is_active ? "✅ Activo" : "❌ Inactivo"}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setModal({ mode: "edit", method: m })}
                            data-testid={`pm-edit-${i}`}
                            className="p-1.5 rounded-lg text-zinc-500 hover:text-yellow-400 hover:bg-yellow-500/10 transition-all"
                            title="Editar"
                          >
                            <PencilSimple className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m)}
                            data-testid={`pm-delete-${i}`}
                            className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                            title="Eliminar"
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          )}
        </AdminSection>

        <AdminSection
          icon={Lightbulb}
          title="Consejo"
          subtitle="Los cambios se aplican al instante"
          color="cyan"
        >
          <p className="text-xs text-zinc-400">
            Los cambios se reflejan inmediatamente. El cliente verá los datos actualizados la próxima vez que vaya a pagar.
          </p>
        </AdminSection>
      </div>
    </div>
  );
}
